'use client'

import { useState, useEffect } from 'react'
import { FileText, Plus, User, Briefcase, Eye, Bell } from 'lucide-react'
import BriefCompanyProfileForm from './CompanyProfileForm'
import BriefForm from './BriefForm'
import BriefView from './BriefView'
import NotificationsPanel from './NotificationsPanel'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { Language } from '@/lib/brief613/translations'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtnInline } from './ui'

interface Brief {
  id: string
  objective: string | null
  targetAudience: string | null
  desiredReaction: string | null
  keyMessage: string | null
  tone: string | null
  deliverables: string | null
  deadline: string | null
  status: string
  createdAt: string
  reference?: string | null
  companyProfile: {
    companyName: string
    industry: string
  } | null
}

interface ClientDashboardProps {
  userId: string
  language?: Language
}

export default function ClientDashboard({ userId, language: languageProp }: ClientDashboardProps) {
  const [language, setLanguage] = useState<Language>(languageProp || 'en')
  const t = useBrief613Translation(language)

  useEffect(() => {
    if (languageProp) {
      setLanguage(languageProp)
      return
    }
    // Load language preference from localStorage
    const savedLang = localStorage.getItem('brief613_language') as Language
    if (savedLang && ['en', 'fr', 'ar', 'ru', 'zh'].includes(savedLang)) {
      setLanguage(savedLang)
    }
  }, [languageProp])
  const [briefs, setBriefs] = useState<Brief[]>([])
  const [hasProfile, setHasProfile] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentView, setCurrentView] = useState<'dashboard' | 'profile' | 'new-brief' | 'view-brief' | 'notifications'>('dashboard')
  const [selectedBriefId, setSelectedBriefId] = useState<string | null>(null)
  const [unreadNotifications, setUnreadNotifications] = useState(0)

  useEffect(() => {
    loadData()
    loadNotifications()
    // Refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [userId])

  const loadNotifications = async () => {
    try {
      const response = await fetch('/api/brief613/notifications?unread_only=true', {
        headers: {
          'x-user-id': userId,
        },
      })
      const data = await response.json()
      if (data.success) {
        setUnreadNotifications(data.notifications.length)
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
    }
  }

  const loadData = async () => {
    try {
      const [briefsRes, userRes] = await Promise.all([
        fetch('/api/brief613/briefs', {
          headers: {
            'x-user-id': userId,
            'x-user-role': 'client',
          },
        }),
        fetch('/api/brief613/users/me', {
          headers: {
            'x-user-id': userId,
          },
        }),
      ])

      const briefsData = await briefsRes.json()
      const userData = await userRes.json()

      if (briefsData.success) {
        setBriefs(briefsData.briefs)
      }

      if (userData.success) {
        setHasProfile(!!userData.user.companyProfile)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewBrief = (briefId: string) => {
    setSelectedBriefId(briefId)
    setCurrentView('view-brief')
  }

  const handleNewBrief = () => {
    if (!hasProfile) {
      setCurrentView('profile')
      return
    }
    setCurrentView('new-brief')
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 max-w-6xl mx-auto text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className={mutedClass}>Loading dashboard...</p>
      </div>
    )
  }

  if (currentView === 'profile') {
    return (
      <BriefCompanyProfileForm
        userId={userId}
        onSuccess={() => {
          setHasProfile(true)
          setCurrentView('dashboard')
          loadData()
        }}
        onCancel={() => setCurrentView('dashboard')}
      />
    )
  }

  if (currentView === 'new-brief') {
    return (
      <BriefForm
        userId={userId}
        onSuccess={() => {
          setCurrentView('dashboard')
          loadData()
        }}
        onCancel={() => setCurrentView('dashboard')}
      />
    )
  }

  if (currentView === 'view-brief' && selectedBriefId) {
    return (
      <BriefView
        briefId={selectedBriefId}
        userId={userId}
        onBack={() => {
          setCurrentView('dashboard')
          setSelectedBriefId(null)
          loadData()
          loadNotifications()
        }}
      />
    )
  }

  if (currentView === 'notifications') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className={`${panel} p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <h2 className={headingClass}>Notifications</h2>
            <button
              onClick={() => {
                setCurrentView('dashboard')
                loadNotifications()
              }}
              className={ghostBtn}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <NotificationsPanel userId={userId} />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className={`${panel} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={headingClass}>My Briefs</h2>
            <p className={`${mutedClass} mt-1`}>Manage your creative briefs</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentView('notifications')}
              className={`${ghostBtn} relative`}
            >
              <Bell size={16} />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/80 text-white text-[10px] flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
            <button onClick={handleNewBrief} className={primaryBtnInline}>
              <Plus size={16} />
              New Brief
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${panel} p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
              <FileText size={18} className="text-white/50" />
            </div>
            <div>
              <p className="text-xl font-semibold text-white/90">{briefs.length}</p>
              <p className="text-white/45 text-xs">Total Briefs</p>
            </div>
          </div>
        </div>

        <div className={`${panel} p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
              <Briefcase size={18} className="text-white/50" />
            </div>
            <div>
              <p className="text-xl font-semibold text-white/90">
                {briefs.filter((b) => b.status === 'submitted').length}
              </p>
              <p className="text-white/45 text-xs">Submitted</p>
            </div>
          </div>
        </div>

        <div className={`${panel} p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
              <User size={18} className="text-white/50" />
            </div>
            <div>
              <p className="text-xl font-semibold text-white/90">{hasProfile ? '✓' : '✗'}</p>
              <p className="text-white/45 text-xs">Profile Status</p>
            </div>
          </div>
        </div>
      </div>

      {/* Briefs List */}
      <div className={`${panel} p-6`}>
        <h3 className={`${headingClass} mb-4`}>Recent Briefs</h3>
        {briefs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto mb-4 text-white/30" size={32} />
            <p className={`${mutedClass} mb-6`}>No briefs yet. Create your first brief to get started!</p>
            <button onClick={handleNewBrief} className={`${primaryBtnInline} mx-auto`}>
              <Plus size={16} />
              Create Your First Brief
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {briefs.map((brief) => (
              <div
                key={brief.id}
                className={`${panelInner} p-4 border-l-2 border-l-white/20 cursor-pointer hover:bg-white/[0.04] transition-colors`}
                onClick={() => handleViewBrief(brief.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-white/90 mb-1">
                      {brief.objective || 'Untitled Brief'}
                    </h4>
                    {brief.reference && (
                      <p className="text-xs text-white/40 mb-1 font-mono">
                        Reference: <span className="text-white/60">{brief.reference}</span>
                      </p>
                    )}
                    <p className="text-xs text-white/45 mb-2">
                      {brief.companyProfile?.companyName || 'No company'} • {brief.companyProfile?.industry || 'N/A'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-white/40">
                      <span>{new Date(brief.createdAt).toLocaleDateString()}</span>
                      {brief.deadline && (
                        <span>Deadline: {new Date(brief.deadline).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-2.5 py-0.5 text-[10px] font-medium rounded-md border ${
                        brief.status === 'submitted'
                          ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
                          : brief.status === 'in_progress'
                          ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
                          : brief.status === 'completed'
                          ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
                          : 'bg-white/5 text-white/50 border-white/10'
                      }`}
                    >
                      {brief.status}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewBrief(brief.id)
                      }}
                      className="text-white/45 hover:text-white/85"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
